import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import {
  counterSignCollaborativeAgreement, CounterSignCollaborativeAgreementParams,
  createCollaborativePhysicianMatch, CreateCollaborativePhysicianParams, deleteCollaborativePhysicianMatch,
  DeleteCollaborativePhysicianParams, getAllCollaborativePhysicians, getCollaborativePhysicianMatchAgreement,
  getCollaborativePhysicianMatchesForPhysician, getCollaborativePhysicianMatchesForProvider, isApiError,
  signCollaborativeAgreement, SignCollaborativeAgreementParams, getCollaborativePhysicianMatch,
  getCollaborativeMatchesForPhysicianAndProvider,
  getCollaborativePhysician,
  setPhysicianCollaborationAvailable
} from '@uplift/api';
import sortBy from 'lodash/sortBy';


export const useAllCollaborativePhysiciansQuery = () => {
  return useSuspenseQuery({
    queryKey: [ 'collaborativePhysician' ],
    queryFn: async () => {
      const response = await getAllCollaborativePhysicians();
      const physicians = response.data.map(item => ({
        ...item,
        id: item.providerId,
        name: `${item.firstName} ${item.lastName}`,
      }));

      return sortBy(physicians, 'name');
    }
  });
};

export const useCollaborativePhysicianQuery = (providerId: string) => {
  return useSuspenseQuery({
    queryKey: [ 'collaborativePhysician', providerId ],
    queryFn: async () => {
      const response = await getCollaborativePhysician(providerId);

      if (response.data) {
        return {
          ...response.data,
          id: response.data.providerId,
          name: `${response.data.firstName} ${response.data.lastName}`,
        };
      }

      return null;
    }
  });
};


export const useCollaboratePhysicianMatchesQuery = (collaborativePhysicianId: string) => {
  return useSuspenseQuery({
    queryKey: [ 'collaborativePhysician', 'matches', collaborativePhysicianId ],
    queryFn: async () => {
      const response = await getCollaborativePhysicianMatchesForPhysician(collaborativePhysicianId);

      return response.data;
    },
  });
};

export const useCollaboratePhysicianMatchesForProviderQuery = (providerId: string) => {
  return useSuspenseQuery({
    queryKey: [ 'collaborativePhysician', 'matches', providerId ],
    queryFn: async () => {
      const response = await getCollaborativePhysicianMatchesForProvider(providerId);

      return response.data;
    },
  });
};

export const useCollaboratePhysicianMatchQuery = (matchId: string) => {
  return useSuspenseQuery({
    queryKey: [ 'collaborativePhysician', 'matches', matchId ],
    queryFn: async () => {
      const response = await getCollaborativePhysicianMatch(matchId);

      return response.data;
    },
  });
};

export const useCollaboratePhysicianMatchForProviderAndStateQuery = (providerId: string, stateId: string) => {
  return useSuspenseQuery({
    queryKey: [ 'collaborativePhysician', 'matches', providerId ],
    queryFn: async () => {
      const response = await getCollaborativePhysicianMatchesForProvider(providerId);

      return response.data;
    },
    select: (matches) => matches.find(m => m.stateId === stateId),
  });
};

export const useCollaboratePhysicianMatchByIdForProviderQuery = (providerId: string, matchId: string) => {
  return useSuspenseQuery({
    queryKey: [ 'collaborativePhysician', 'matches', providerId ],
    queryFn: async () => {
      const response = await getCollaborativePhysicianMatchesForProvider(providerId);

      return response.data;
    },
    select: (matches) => matches.find(m => m.id === matchId),
  });
};

export const useCollaboratePhysicianMatchByIdForPhysicianQuery = (collaborativePhysicianId: string, matchId: string) => {
  return useSuspenseQuery({
    queryKey: [ 'collaborativePhysician', 'matches', collaborativePhysicianId ],
    queryFn: async () => {
      const response = await getCollaborativePhysicianMatchesForPhysician(collaborativePhysicianId);

      return response.data;
    },
    select: (matches) => matches.find(m => m.id === matchId),
  });
};


export const useCollaboratePhysicianAndProviderMatchesQuery = (physicianId: string, providerId: string) => {
  return useSuspenseQuery({
    queryKey: [ 'collaborativePhysician', 'matches', physicianId, providerId ],
    queryFn: async () => {
      const response = await getCollaborativeMatchesForPhysicianAndProvider(physicianId, providerId);

      return response.data;
    },
  });
};


export const useCollaboratePhysicianMatchAgreementQuery = (matchId: string) => {
  return useSuspenseQuery({
    queryKey: [ 'collaborativePhysician', 'match', matchId, 'agreement' ],
    queryFn: async () => {
      try {
        const response = await getCollaborativePhysicianMatchAgreement(matchId);

        return response.data;
      } catch {
        return null;
      }
    },
  });
};


export const useSetPhysicianCollaborationAvailableMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (values: { licenseId: string; available: boolean }) => {
      await setPhysicianCollaborationAvailable(values.licenseId, values.available);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [ 'collaborativePhysician' ] });
    },
    onError: async (error) => {
      if (isApiError(error)) {
        if (error?.response?.data.error_key === 'seat_capacity_reached') {
          return;
        }
        if (error?.response?.data.error) {
          throw new Error(error.response.data.error);
        } else {
          throw new Error('An error occurred while adding match');
        }
      } else {
        throw new Error('An error occurred while adding match');
      }
    },
  });
};


export const useCreateCollaborativePhysicianMatchMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (values: CreateCollaborativePhysicianParams) => {
      await createCollaborativePhysicianMatch(values);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [ 'collaborativePhysician' ] });
    },
    onError: async (error) => {
      if (isApiError(error)) {
        if (error?.response?.data.error_key === 'seat_capacity_reached') {
          return;
        }
        if (error?.response?.data.error) {
          throw new Error(error.response.data.error);
        } else {
          throw new Error('An error occurred while adding match');
        }
      } else {
        throw new Error('An error occurred while adding match');
      }
    },
  });
};


export const useDeleteCollaborativePhysicianMatchMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (values: DeleteCollaborativePhysicianParams) => {
      await deleteCollaborativePhysicianMatch(values);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [ 'collaborativePhysician' ] });
    },
    onError: async (error) => {
      if (isApiError(error)) {
        if (error?.response?.data.error) {
          throw new Error(error.response.data.error);
        } else {
          throw new Error('An error occurred while deleting match');
        }
      } else {
        throw new Error('An error occurred while deleting match');
      }
    },
  });
};


export const useSignCollaborativeAgreementMutation = (providerId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (values: SignCollaborativeAgreementParams) => {
      const response = await signCollaborativeAgreement(values);

      return response.data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [ 'user', providerId, 'documents' ] });
    },
    onError: async (error) => {
      if (isApiError(error)) {
        if (error?.response?.data.error) {
          throw new Error(error.response.data.error);
        } else {
          throw new Error('An error occurred while signing the agreement');
        }
      } else {
        throw new Error('An error occurred while signing the agreement');
      }
    },
  });
};


export const useCounterSignCollaborativeAgreementMutation = (physicianId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (values: CounterSignCollaborativeAgreementParams) => {
      const response = await counterSignCollaborativeAgreement(values);

      return response.data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [ 'user', physicianId, 'documents' ] });
    },
    onError: async (error) => {
      if (isApiError(error)) {
        if (error?.response?.data.error) {
          throw new Error(error.response.data.error);
        } else {
          throw new Error('An error occurred while counter-signing the agreement');
        }
      } else {
        throw new Error('An error occurred while counter-signing the agreement');
      }
    },
  });
};
